// Browser-direct, resumable upload of a content session into the media store
// (ADR-021 §3). Plain fetch against the Blob REST API under a directory-scoped
// write SAS; no file byte passes through a portal replica.
//
// Memory stays bounded at roughly MEDIA_CONTENT_MAX_INFLIGHT × block size: work
// is generated lazily, each worker reads one File.slice at a time, and the slice
// is released as soon as its PUT settles. A 100 GB file never sits in memory.
//
// The SAS and the request URL are never logged or attached to an error: every
// failure surfaces as an UploadEngineError carrying only a code and a status.

import { createMD5, type IHasher } from 'hash-wasm';

import {
  MEDIA_CONTENT_MAX_INFLIGHT,
  MEDIA_CONTENT_SAS_REFRESH_LEAD_MS,
  mediaContentBlockId,
  type MediaContentSas,
} from 'shared';

const BLOB_API_VERSION = '2023-11-03';
const MAX_ATTEMPTS = 8;
const MAX_AUTH_REFRESHES = 3;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 30_000;
const THROUGHPUT_WINDOW_MS = 15_000;
const PROGRESS_INTERVAL_MS = 250;

export type UploadEngineErrorCode =
  | 'network'
  | 'rejected'
  | 'session_closed'
  | 'unreadable'
  | 'cancelled';

export class UploadEngineError extends Error {
  readonly code: UploadEngineErrorCode;
  readonly status: number | null;
  constructor(code: UploadEngineErrorCode, status: number | null = null) {
    super(code);
    this.code = code;
    this.status = status;
  }
}

export type UploadJob = {
  file: File;
  relative_path: string;
  // Block ids the store already holds uncommitted for this path (resume). Only
  // consulted for a file large enough to go up in blocks.
  staged_block_ids: ReadonlySet<string>;
};

export type UploadProgress = {
  bytes_done: number;
  bytes_total: number;
  files_done: number;
  files_total: number;
  bytes_per_second: number | null;
  eta_seconds: number | null;
};

function encodePath(relative_path: string): string {
  return relative_path.split('/').map(encodeURIComponent).join('/');
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function blockListXml(ids: readonly string[]): string {
  return `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids
    .map((id) => `<Latest>${id}</Latest>`)
    .join('')}</BlockList>`;
}

function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function backoffMs(attempt: number, retryAfter: string | null): number {
  const hinted = retryAfter ? Number(retryAfter) * 1000 : NaN;
  if (Number.isFinite(hinted) && hinted > 0) return Math.min(hinted, BACKOFF_CAP_MS);
  const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2));
}

type FileState = {
  job: UploadJob;
  ids: string[];
  outstanding: number;
};

type WorkItem =
  | { kind: 'blob'; state: FileState }
  | { kind: 'block'; state: FileState; index: number; start: number; end: number }
  | { kind: 'commit'; state: FileState };

type RequestSpec = {
  query: string;
  headers: Record<string, string>;
  body: BodyInit;
};

export class MediaContentUploadEngine {
  private readonly blockSize: number;
  private readonly refreshSas: () => Promise<MediaContentSas>;
  private readonly onProgress: (p: UploadProgress) => void;
  private sas: MediaContentSas;
  private sasRefresh: Promise<void> | null = null;
  private readonly states: FileState[];
  private readonly queue: Generator<WorkItem>;
  private readonly controllers = new Set<AbortController>();
  private paused = false;
  private resumeWaiters: Array<() => void> = [];
  private stopped: UploadEngineError | null = null;
  private bytesDone: number;
  private readonly bytesTotal: number;
  private filesDone: number;
  private readonly filesTotal: number;
  private samples: Array<{ at: number; bytes: number }> = [];
  private lastEmit = 0;
  private readonly sleepers = new Set<() => void>();

  constructor(opts: {
    sas: MediaContentSas;
    block_size: number;
    jobs: readonly UploadJob[];
    // Bytes and files of the session already in the store (a resume), so
    // progress reads against the whole session rather than the remainder.
    already_bytes: number;
    already_files: number;
    total_bytes: number;
    total_files: number;
    refreshSas: () => Promise<MediaContentSas>;
    onProgress: (p: UploadProgress) => void;
  }) {
    this.sas = opts.sas;
    this.blockSize = opts.block_size;
    this.refreshSas = opts.refreshSas;
    this.onProgress = opts.onProgress;
    this.bytesTotal = opts.total_bytes;
    this.filesTotal = opts.total_files;
    this.bytesDone = opts.already_bytes;
    this.filesDone = opts.already_files;

    this.states = opts.jobs.map((job) => {
      const single = job.file.size <= this.blockSize;
      const n = single ? 1 : Math.ceil(job.file.size / this.blockSize);
      const ids = Array.from({ length: n }, (_, i) => mediaContentBlockId(i));
      let outstanding = n;
      if (!single) {
        ids.forEach((id, i) => {
          if (!job.staged_block_ids.has(id)) return;
          outstanding -= 1;
          this.bytesDone += Math.min(job.file.size, (i + 1) * this.blockSize) - i * this.blockSize;
        });
      }
      return { job, ids, outstanding };
    });
    this.queue = this.generate();
  }

  private *generate(): Generator<WorkItem> {
    for (const st of this.states) {
      const size = st.job.file.size;
      if (size <= this.blockSize) {
        yield { kind: 'blob', state: st };
        continue;
      }
      if (st.outstanding === 0) {
        yield { kind: 'commit', state: st };
        continue;
      }
      for (let i = 0; i < st.ids.length; i += 1) {
        if (st.job.staged_block_ids.has(st.ids[i]!)) continue;
        const start = i * this.blockSize;
        yield {
          kind: 'block',
          state: st,
          index: i,
          start,
          end: Math.min(size, start + this.blockSize),
        };
      }
    }
  }

  async run(): Promise<void> {
    this.emit();
    await Promise.all(Array.from({ length: MEDIA_CONTENT_MAX_INFLIGHT }, () => this.worker()));
    if (this.stopped) throw this.stopped;
    this.emit();
  }

  pause(): void {
    if (this.paused || this.stopped) return;
    this.paused = true;
    for (const c of this.controllers) c.abort();
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.samples = [];
    const waiters = this.resumeWaiters;
    this.resumeWaiters = [];
    for (const w of waiters) w();
    this.emit();
  }

  cancel(): void {
    this.stop(new UploadEngineError('cancelled'));
  }

  private stop(err: UploadEngineError): void {
    if (this.stopped) return;
    this.stopped = err;
    for (const c of this.controllers) c.abort();
    for (const wake of this.sleepers) wake();
    this.sleepers.clear();
    this.paused = false;
    const waiters = this.resumeWaiters;
    this.resumeWaiters = [];
    for (const w of waiters) w();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const wake = (): void => {
        clearTimeout(timer);
        this.sleepers.delete(wake);
        resolve();
      };
      const timer = setTimeout(wake, ms);
      this.sleepers.add(wake);
    });
  }

  private waitWhilePaused(): Promise<void> {
    if (!this.paused) return Promise.resolve();
    return new Promise((resolve) => this.resumeWaiters.push(resolve));
  }

  private async worker(): Promise<void> {
    let hasher: IHasher | null = null;
    for (;;) {
      if (this.stopped) return;
      await this.waitWhilePaused();
      if (this.stopped) return;
      const next = this.queue.next();
      if (next.done) return;
      try {
        hasher ??= await createMD5();
        await this.perform(next.value, hasher);
      } catch (err) {
        this.stop(err instanceof UploadEngineError ? err : new UploadEngineError('network'));
        return;
      }
    }
  }

  private async perform(item: WorkItem, hasher: IHasher): Promise<void> {
    const { state } = item;
    if (item.kind === 'commit') {
      await this.commit(state);
      return;
    }
    const slice =
      item.kind === 'blob' ? state.job.file : state.job.file.slice(item.start, item.end);
    let bytes: Uint8Array<ArrayBuffer>;
    try {
      bytes = new Uint8Array(await slice.arrayBuffer());
    } catch {
      throw new UploadEngineError('unreadable');
    }
    hasher.init();
    hasher.update(bytes);
    const contentMd5 = toBase64(hasher.digest('binary'));

    if (item.kind === 'blob') {
      await this.send(state.job.relative_path, {
        query: '',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/octet-stream',
          'Content-MD5': contentMd5,
        },
        body: bytes,
      });
      this.advance(bytes.byteLength, true);
      return;
    }

    await this.send(state.job.relative_path, {
      query: `comp=block&blockid=${encodeURIComponent(state.ids[item.index]!)}`,
      headers: { 'Content-MD5': contentMd5 },
      body: bytes,
    });
    this.advance(bytes.byteLength, false);
    state.outstanding -= 1;
    if (state.outstanding === 0) await this.commit(state);
  }

  private async commit(state: FileState): Promise<void> {
    await this.send(state.job.relative_path, {
      query: 'comp=blocklist',
      headers: {
        'Content-Type': 'application/xml',
        'x-ms-blob-content-type': 'application/octet-stream',
      },
      body: blockListXml(state.ids),
    });
    this.advance(0, true);
  }

  private advance(bytes: number, fileDone: boolean): void {
    this.bytesDone += bytes;
    if (fileDone) this.filesDone += 1;
    if (Date.now() - this.lastEmit >= PROGRESS_INTERVAL_MS) this.emit();
  }

  private emit(): void {
    const now = Date.now();
    this.lastEmit = now;
    this.samples.push({ at: now, bytes: this.bytesDone });
    while (this.samples.length > 2 && now - this.samples[0]!.at > THROUGHPUT_WINDOW_MS) {
      this.samples.shift();
    }
    const first = this.samples[0]!;
    const elapsed = (now - first.at) / 1000;
    const rate = elapsed >= 1 ? (this.bytesDone - first.bytes) / elapsed : null;
    const remaining = Math.max(0, this.bytesTotal - this.bytesDone);
    this.onProgress({
      bytes_done: this.bytesDone,
      bytes_total: this.bytesTotal,
      files_done: this.filesDone,
      files_total: this.filesTotal,
      bytes_per_second: rate,
      eta_seconds: rate && rate > 0 ? Math.round(remaining / rate) : null,
    });
  }

  private async ensureFreshSas(force: boolean): Promise<void> {
    const expires = Date.parse(this.sas.expires_at);
    const due =
      !Number.isFinite(expires) || Date.now() >= expires - MEDIA_CONTENT_SAS_REFRESH_LEAD_MS;
    if (!force && !due) return;
    this.sasRefresh ??= (async () => {
      try {
        for (let attempt = 0; ; attempt += 1) {
          if (this.stopped) throw this.stopped;
          try {
            this.sas = await this.refreshSas();
            return;
          } catch (err) {
            const response = (
              err as { response?: { status?: number; headers?: Record<string, string> } }
            ).response;
            const status = response?.status ?? null;
            if (status === 403 || status === 404 || status === 409) {
              throw new UploadEngineError('session_closed', status);
            }
            const transient = status === null || isTransientStatus(status);
            if (!transient || attempt >= MAX_ATTEMPTS - 1) {
              throw new UploadEngineError(transient ? 'network' : 'rejected', status);
            }
            await this.sleep(backoffMs(attempt, response?.headers?.['retry-after'] ?? null));
          }
        }
      } finally {
        this.sasRefresh = null;
      }
    })();
    await this.sasRefresh;
  }

  private async send(relative_path: string, spec: RequestSpec): Promise<void> {
    let attempt = 0;
    let authRefreshes = 0;
    for (;;) {
      if (this.stopped) throw this.stopped;
      await this.waitWhilePaused();
      if (this.stopped) throw this.stopped;
      await this.ensureFreshSas(false);

      const query = spec.query ? `${spec.query}&${this.sas.sas}` : this.sas.sas;
      const url = `${this.sas.prefix_url}/${encodePath(relative_path)}?${query}`;
      const controller = new AbortController();
      this.controllers.add(controller);
      let response: Response | null = null;
      try {
        response = await fetch(url, {
          method: 'PUT',
          headers: { 'x-ms-version': BLOB_API_VERSION, ...spec.headers },
          body: spec.body,
          signal: controller.signal,
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
      } catch {
        response = null;
      } finally {
        this.controllers.delete(controller);
      }

      if (this.stopped) throw this.stopped;
      if (controller.signal.aborted) continue;
      if (response?.ok) return;

      if (response?.status === 403 && authRefreshes < MAX_AUTH_REFRESHES) {
        authRefreshes += 1;
        await this.ensureFreshSas(true);
        continue;
      }
      if (response && !isTransientStatus(response.status)) {
        throw new UploadEngineError(
          response.status === 403 || response.status === 404 ? 'session_closed' : 'rejected',
          response.status
        );
      }
      if (attempt >= MAX_ATTEMPTS - 1) {
        throw new UploadEngineError('network', response?.status ?? null);
      }
      const wait = backoffMs(attempt, response?.headers.get('Retry-After') ?? null);
      attempt += 1;
      await this.sleep(wait);
    }
  }
}
